import axios, { AxiosError } from 'axios';
import { useMutation, UseMutationOptions, useQueryClient } from 'react-query';

import { createRestApi, DefaultUpdateOrCreateResponse } from '~/App/Api/rest';
import type { UploadUrlResponse } from '~/types/api';
import {
  TestimonialRespondent,
  TestimonialResponse,
  TestimonialAttachment,
  TestimonialResponseType,
} from '~/types/models/testimonial';
import { queryKeys } from '~/App/Api/TestimonialRespondent';
import { RespondentApiArgs } from '~/App/Modules/Testimonials/Actions/respondentsAction';
import { RichText, parseRichTextToSlateData } from '@vimily/bonjoro-ui-components-v2/RichTextEditor';

export interface Respondent {
  email: string;
  name: string;
  metadata?: string;
}

export function create({
  testimonialUuid,
  name,
  email,
  metadata,
}: Respondent & { testimonialUuid: string }) {
  const api = createRestApi<TestimonialRespondent>(
    `/api/v3/testimonials/${testimonialUuid}/respondents`
  );
  return api.post({
    name,
    email,
    metadata,
  });
}

export async function getRespondent(
  testimonialUuid: string,
  respondentUuid: string
) {
  const api = createRestApi<{ data: TestimonialRespondent }>(
    `/api/v3/testimonials/${testimonialUuid}/respondents`
  );
  const res = await api.get(respondentUuid);
  return res.data;
}

export interface BaseRespondentParams {
  testimonialUuid: string;
  respondentUuid: string;
}
export interface UpdateRespondentPositionParams extends BaseRespondentParams {
  position: string;
}
export interface SubmitRespondentResponseParams extends BaseRespondentParams {
  hasConsent: boolean;
}

export interface UpdateRespondentMetadataParams extends BaseRespondentParams {
  metadata: string;
}

export async function updateRespondentPosition({
  testimonialUuid,
  respondentUuid,
  position,
}: UpdateRespondentPositionParams) {
  const api = createRestApi<{ data: TestimonialRespondent }>(
    `/api/v3/testimonials/${testimonialUuid}/respondents`
  );
  const res = await api.put(respondentUuid, {
    position,
  });
  return res.data;
}

export async function submitRespondentResponse({
  testimonialUuid,
  respondentUuid,
  hasConsent,
}: SubmitRespondentResponseParams) {
  const api = createRestApi<{ data: TestimonialRespondent }>(
    `/api/v3/testimonials/${testimonialUuid}/respondents/${respondentUuid}/submit`
  );
  const res = await api.post({
    has_consent: hasConsent,
  });
  return res.data;
}

export interface UploadUrlParams {
  respondentId: string;
  questionId: string;
}
export async function getUploadUrl({
  respondentId,
  questionId,
}: UploadUrlParams) {
  const uploadUrlResponse = await axios.get<UploadUrlResponse>(
    `/api/v3/respondents/${respondentId}/questions/${questionId}/video-url`
  );

  return uploadUrlResponse.data;
}

export interface SaveResponseParams {
  respondentId: string;
  questionId: string;
  responseId: string;
  type: TestimonialResponseType;
  textResponse?: RichText;
  thumbnail?: Blob;
  thumbnailFull?: Blob;
  timestamp?: number;
  tags?: string[];
}

export async function saveResponse({
  respondentId,
  type,
  textResponse,
  questionId,
  thumbnail,
  thumbnailFull,
  timestamp,
}: SaveResponseParams) {
  const formData = new FormData();
  formData.append('testimonial_question_uuid', questionId);
  formData.append('type', type);

  if (timestamp) {
    formData.append('timestamp', timestamp.toString());
  }

  if (thumbnail) {
    formData.append('thumbnail', thumbnail);
  }

  if (thumbnailFull) {
    formData.append('thumbnail_full', thumbnailFull);
  }

  if (textResponse) {
    formData.append(
      'text_response_rich',
      JSON.stringify(parseRichTextToSlateData(textResponse))
    );
  }

  const res = await axios.post<
    DefaultUpdateOrCreateResponse<TestimonialResponse>
  >(`/api/v3/respondents/${respondentId}/response`, formData);

  return res.data;
}

export async function updateResponse({
  respondentId,
  type,
  textResponse,
  thumbnail,
  thumbnailFull,
  responseId,
  timestamp,
  tags,
}: SaveResponseParams) {
  const formData = new FormData();
  formData.append('_method', 'PUT');
  formData.append('type', type);

  if (timestamp) {
    formData.append('timestamp', timestamp.toString());
  }

  if (tags) {
    formData.append('tags', JSON.stringify(tags));
  }

  if (thumbnail) {
    formData.append('thumbnail', thumbnail);
  }

  if (thumbnailFull) {
    formData.append('thumbnail_full', thumbnailFull);
  }

  if (textResponse) {
    formData.append(
      'text_response_rich',
      JSON.stringify(parseRichTextToSlateData(textResponse))
    );
  }

  const res = await axios.post<
    DefaultUpdateOrCreateResponse<TestimonialResponse>
  >(`/api/v3/respondents/${respondentId}/response/${responseId}`, formData);

  return res.data;
}

export interface DeleteResponseArgs {
  responseUuid: string;
}

export async function deleteResponse({ responseUuid }: DeleteResponseArgs) {
  const res = await axios.delete<{ message: string }>(
    `/api/v3/testimonials/response/${responseUuid}`
  );

  return res.data;
}

type UseUpdateResponseArgs = {
  params?: RespondentApiArgs;
  options?: UseMutationOptions<
    TestimonialResponse,
    AxiosError,
    SaveResponseParams
  >;
};

export const useUpdateResponse = ({
  params,
  options,
}: UseUpdateResponseArgs) => {
  const queryClient = useQueryClient();

  return useMutation(updateResponse, {
    onSuccess: () => {
      if (params) {
        void queryClient.invalidateQueries(queryKeys.detail(params));
      }
    },
    ...options,
  });
};

type UseDeleteResponseArgs = {
  params?: RespondentApiArgs;
  options?: UseMutationOptions<
    { message: string },
    AxiosError,
    DeleteResponseArgs
  >;
};

export const useDeleteResponse = ({
  params,
  options,
}: UseDeleteResponseArgs) => {
  const queryClient = useQueryClient();

  return useMutation(deleteResponse, {
    onSuccess: () => {
      if (params) {
        void queryClient.invalidateQueries(queryKeys.detail(params));
      }
    },
    ...options,
  });
};

export interface UploadAttachmentUrlParams {
  respondentId: string;
  attachmentId: string;
  extension: string;
  metadata: Record<string, string>;
}

export async function uploadAttachmentUrl({
  respondentId,
  attachmentId,
  extension,
  metadata,
}: UploadAttachmentUrlParams) {
  const uploadUrlResponse = await axios.post<UploadUrlResponse>(
    `/api/v3/respondents/${respondentId}/attachment-request/${attachmentId}/media-upload`,
    {
      file_extension: extension,
      metadata,
    }
  );

  return uploadUrlResponse.data;
}

export interface SaveAttachmentParams {
  respondentId: string;
  attachmentId: string;
  textResponse: string;
}

export async function saveAttachment({
  respondentId,
  attachmentId,
  textResponse,
}: SaveAttachmentParams) {
  const res = await axios.post<TestimonialAttachment>(
    `/api/v3/respondents/${respondentId}/attachments`,
    {
      text_response: textResponse,
      attachment_request_uuid: attachmentId,
    }
  );

  return res.data;
}

export interface UpdateAttachmentParams {
  respondentId: string;
  requestResponseId: string;
  textResponse?: string;
}

export async function updateAttachment({
  respondentId,
  requestResponseId,
  textResponse,
}: UpdateAttachmentParams) {
  const res = await axios.put<TestimonialAttachment>(
    `/api/v3/respondents/${respondentId}/attachments/${requestResponseId}`,
    {
      text_response: textResponse,
    }
  );

  return res.data;
}

export interface DeleteAttachmentParams {
  respondentId: string;
  requestResponseId: string;
}

export async function deleteAttachment({
  respondentId,
  requestResponseId,
}: DeleteAttachmentParams) {
  const res = await axios.delete<TestimonialAttachment>(
    `/api/v3/respondents/${respondentId}/attachments/${requestResponseId}`
  );
  return res.data;
}

export async function updateRespondentMetadata({
  testimonialUuid,
  respondentUuid,
  metadata,
}: UpdateRespondentMetadataParams) {
  const res = await axios.put<{ message: string }>(
    `/api/v3/testimonials/${testimonialUuid}/respondents/${respondentUuid}/metadata`,
    {
      metadata,
    }
  );

  return res.data;
}
